import React, { useEffect, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { DialogContent, Button, Grid, Alert, useMediaQuery, useTheme } from '@mui/material'
import { useForm, FormProvider } from 'react-hook-form'
import { isYearlyRecurrence } from 'utils/service'

import { useCSDetailsCachedData } from 'components/configured-service/useCSDetailsCachedData'
import { useServiceDetailsFormSubmission } from 'components/configured-service/accordions/ServiceDetailsAccordion/useServiceDetailsFormSubmission'
import { useServiceDetailsFormDefaults } from 'components/configured-service/accordions/ServiceDetailsAccordion/useServiceDetailsFormDefaults'
import HHBaseDialog from 'components/common/HHBaseDialog'
import { useLazyFetchConfiguredServiceEventsQuery } from 'api/configured-service/configuredServiceCrud'
import { selectScheduledServiceEvents, selectStartDateAsDate } from 'data/configured-service/configuredServiceDetailsSelectors'
import { selectServiceActions } from 'data/meta/metaSelectors'
import T from 'T'
import HHDialogActions from 'components/common/HHDialogActions'
import HHDialogTitle from 'components/common/HHDialogTitle'
import EditServiceRecurrenceForm from './components/EditServiceRecurrenceForm'
import EditServiceRecurrenceEventsPreview from './components/EditServiceRecurrenceEventsPreview'
import { getUniqueRecurrenceOptions, findRecurrenceOptionByKey } from './utils/recurrenceOptionsHelper'
import { useFrequencyArrayAdjustment } from './hooks/useFrequencyArrayAdjustment'
import { useIntervalTransition } from './hooks/useIntervalTransition'
import { useFormSubmission } from './hooks/useFormSubmission'
import { getDialogInitialState } from './utils/dialogState'

const EditServiceRecurrenceDialog = ({ open, onClose, initialData, serviceId, metaFiltersData, onRecurrenceUpdate }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const defaultValues = getDialogInitialState(initialData)
  const { data: serviceData } = useCSDetailsCachedData({ id: serviceId })
  const serviceFormDefaults = useServiceDetailsFormDefaults(serviceData, metaFiltersData)
  const { formatFormData } = useServiceDetailsFormSubmission(serviceId)
  const recurrenceOptions = getUniqueRecurrenceOptions(metaFiltersData)

  const startDate = useMemo(() => selectStartDateAsDate(serviceData), [serviceData])

  const [fetchCSEvents, { data: eventsData }] = useLazyFetchConfiguredServiceEventsQuery()

  const serviceActions = useMemo(() => selectServiceActions(metaFiltersData), [metaFiltersData])

  const existingEvents = useMemo(() => {
    if (eventsData && serviceActions) {
      return selectScheduledServiceEvents({ events: eventsData, actions: serviceActions, isDraft: false })
    }
    return []
  }, [eventsData, serviceActions])

  const methods = useForm({
    defaultValues,
    mode: 'onChange',
  })

  const {
    watch,
    reset,
    setValue,
    formState: { isValid },
  } = methods

  const formData = watch()
  const currentFrequency = parseInt(formData.recurrenceFrequency, 10) || 1

  useFrequencyArrayAdjustment(open, currentFrequency, formData, setValue)
  useIntervalTransition(open, formData.serviceInterval, currentFrequency, formData, setValue, metaFiltersData)

  const { handleSubmit, error, setError, isUpdating } = useFormSubmission(
    methods,
    serviceFormDefaults,
    recurrenceOptions,
    formatFormData,
    onClose,
    onRecurrenceUpdate
  )

  const handleCancel = useCallback(() => {
    reset(getDialogInitialState(initialData))
    onClose()
  }, [initialData, onClose, reset])

  useEffect(() => {
    if (open) {
      setError(null)
      reset(getDialogInitialState(initialData))
    }
  }, [open, initialData, reset, setError])

  useEffect(() => {
    if (open && serviceId) {
      fetchCSEvents({ id: serviceId })
    }
  }, [open, serviceId, fetchCSEvents])

  const selectedFrequencyOption = findRecurrenceOptionByKey(recurrenceOptions, formData.serviceInterval)
  const isYearlySelected = isYearlyRecurrence(selectedFrequencyOption?.recurrenceInterval)

  return (
    <FormProvider {...methods}>
      <HHBaseDialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile} paperPropsSx={isMobile ? { maxHeight: '100%' } : undefined}>
        <HHDialogTitle title={T.EDIT_SERVICE_EVENT_FREQUENCY} onClose={onClose} />

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 5 }}>
              <EditServiceRecurrenceForm metaFiltersData={metaFiltersData} showYearly isCompact={isMobile} />
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              {isYearlySelected ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {T.YEARLY_SERVICE_WORK_ORDER_WARNING}
                </Alert>
              ) : (
                <EditServiceRecurrenceEventsPreview
                  open={open}
                  formData={formData}
                  defaultValues={defaultValues}
                  recurrenceOptions={recurrenceOptions}
                  serviceData={serviceData}
                  existingEvents={existingEvents}
                  startDate={startDate}
                />
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <HHDialogActions sx={isMobile ? { paddingBottom: 10 } : {}}>
          <Button onClick={handleCancel} color="inherit" disabled={isUpdating}>
            {T.CANCEL}
          </Button>
          <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isUpdating || !isValid} sx={{ minWidth: 120 }}>
            {isUpdating ? T.LOADING : T.UPDATE}
          </Button>
        </HHDialogActions>
      </HHBaseDialog>
    </FormProvider>
  )
}

EditServiceRecurrenceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  serviceId: PropTypes.string.isRequired,
  metaFiltersData: PropTypes.object,
  onRecurrenceUpdate: PropTypes.func,
  initialData: PropTypes.shape({
    frequency: PropTypes.string,
    serviceInterval: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    daysStructure: PropTypes.object,
    eventInstanceType: PropTypes.string,
    eventOccurrence: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dayOfTheWeek: PropTypes.string,
    dayOfTheMonth: PropTypes.number,
  }),
}

EditServiceRecurrenceDialog.defaultProps = {
  initialData: null,
  metaFiltersData: {},
  onRecurrenceUpdate: null,
}

export default EditServiceRecurrenceDialog
